/**
 * The reading pipeline's contract: the state a reading builds up, the steps
 * that add to it, the spec that orders them and the ports a runner provides.
 *
 * Steps never overwrite each other. Each adds candidates tagged with its own
 * id, and a `resolve` step picks among them by a policy the spec states, so
 * which step decided a field is always on record (idea 22).
 */

import type { CategoryOption } from '../categories.js';
import type { FilingLayout } from '../filing-layout.js';
import type { ProviderImage } from '../providers/core.js';

/** One piece of a line as the OCR engine read it: `SV BÖNOR 380G`, and apart from it `11,90`. */
export interface OcrFragment {
  text: string;
  /** 0..1, the engine's own. */
  confidence: number;
  /** The engine's other readings, best first. */
  candidates: string[];
}

/** One printed row of OCR text, with what the OCR engine said about it when the client kept it. */
export interface OcrLine {
  text: string;
  /** The row's weakest fragment's confidence. */
  confidence?: number;
  /** Other readings of the whole row, best first: the row with one fragment read differently. */
  candidates?: string[];
  /** The fragments the row was put together from, left to right. */
  fragments?: OcrFragment[];
}

export interface ReadingInput {
  /** The OCR lines. Built from `text` by the `lines` step when absent. */
  lines?: OcrLine[];
  /** The OCR text, as stored on a receipt. */
  text?: string | null;
  /** The picture a model is sent. */
  image?: ProviderImage | null;
  /** What "today" is, for dates: fixed in a benchmark, the clock in the app. */
  today: Date;
  /** The categories the receipt may be filed under. */
  categories: readonly CategoryOption[];
  /** The user's own extra instructions for the model. */
  instructions?: string | null;
}

/** The fields of a reading. The header fields are named as in `RECEIPT_JSON_SCHEMA`. */
export const FIELD_KEYS = [
  'merchant', 'orgNumber', 'purchasedAt', 'currency', 'total', 'subtotal', 'discountTotal', 'roundingAmount',
  'depositTotal', 'vatLines', 'paymentMethod', 'cardLast4', 'receiptNumber', 'terminalId', 'cashier', 'confidence',
  'items', 'receiptCategory', 'otherCategories', 'itemCategories',
] as const;

export type FieldKey = (typeof FIELD_KEYS)[number];

export interface Candidate {
  /**
   * As the step found it: a model's field as it wrote it (an amount as a
   * printed string), an OCR step's parsed value. `normalizeExtraction`
   * interprets either.
   */
  value: unknown;
  /** The id of the step that added it. */
  source: string;
  /** 0..1, when the step has a measure. */
  confidence?: number;
  /** Line numbers (1-based) it was read from. */
  evidence?: number[];
  /** For `itemCategories`: the source of the item list the categories file, in its order. */
  forItems?: string;
}

/** The registry's facts about the company on the receipt. */
export interface CompanyInfo {
  orgNumber: string;
  name: string;
  /** The primary SNI (industry) description. */
  industry: string | null;
}

export interface ReadingState {
  input: ReadingInput;
  candidates: Partial<Record<FieldKey, Candidate[]>>;
  /** The candidate chosen for each field, by the last `resolve`. */
  resolved: Partial<Record<FieldKey, Candidate>>;
  company: CompanyInfo | null;
  /** Each model step's answer as parsed JSON, by step id. */
  answers: Record<string, Record<string, unknown>>;
  /** What a step leaves for later steps' conditions, such as `categoriesDropped`. */
  facts: Record<string, unknown>;
  trace: StepTrace[];
}

export interface StepTrace {
  /** The step's id in the spec, which is also the source of its candidates. */
  id: string;
  /** The registered step it ran. */
  step: string;
  status: 'ok' | 'skipped' | 'failed';
  ms: number;
  /** Why it was skipped, or what it failed with. */
  reason?: string;
  /** What it added, one short phrase each. */
  added: string[];
  /** Prompt blocks left out to fit the budget. */
  dropped?: string[];
  /** The model's tokens, when it called one. */
  tokens?: { input: number | null; output: number | null };
  /** For `resolve`: the source chosen per field. */
  chosen?: Partial<Record<FieldKey, string>>;
}

/** What a step hands back. The runner applies it; steps never mutate the state. */
export interface StepOutput {
  candidates?: Partial<Record<FieldKey, Candidate[]>>;
  resolved?: Partial<Record<FieldKey, Candidate>>;
  company?: CompanyInfo | null;
  lines?: OcrLine[];
  answer?: Record<string, unknown>;
  facts?: Record<string, unknown>;
  added?: string[];
  dropped?: string[];
  tokens?: { input: number | null; output: number | null };
  chosen?: Partial<Record<FieldKey, string>>;
  /** The step decided it had nothing to do; traced as skipped. */
  skipped?: string;
}

/**
 * What a step needs before it can run and what it adds, checked when a spec
 * loads: `text`, `lines`, `image`, `categories`, `company`,
 * `candidates:<field>`, `resolved:<field>`, `answer:<step id>`, `transport`,
 * `company-lookup`.
 */
export type Key = string;

export interface StepContext<O> {
  /** The step's id in the spec. */
  id: string;
  options: O;
  ports: Ports;
}

export interface Step<O = unknown> {
  name: string;
  needs(options: O): Key[];
  provides(options: O, id: string): Key[];
  run(state: Readonly<ReadingState>, context: StepContext<O>): Promise<StepOutput>;
}

export interface SpecEntry {
  step: string;
  /** Defaults to the step's name. Distinct ids let a step run twice. */
  id?: string;
  options?: unknown;
  /** A named condition (`CONDITIONS`); the step is skipped when it does not hold. */
  when?: string;
  /** A failure is traced and the reading goes on. A required step's failure fails the reading. */
  optional?: boolean;
}

export type Spec = readonly SpecEntry[];

/** A request to a model: text in, JSON out. */
export interface ModelRequest {
  system: string;
  userPrompt: string;
  schema: Record<string, unknown>;
  schemaName: string;
  image: ProviderImage | null;
  /** A GBNF grammar in place of the schema (`filing-layout.ts`); the answer then comes back as `raw.text`. */
  grammar?: string;
}

export interface ModelResponse {
  raw: Record<string, unknown>;
  model: string;
  inputTokens: number | null;
  outputTokens: number | null;
  /** The endpoint refused the schema and the answer was asked for as plain JSON. */
  structuredOutputFallback?: boolean;
}

/** A model behind a wire format. It knows its limits and nothing about receipts. */
export interface ModelTransport {
  id: string;
  model: string;
  /** Whether it can be sent a picture. */
  images: boolean;
  /** The most a request may take, in estimated tokens. */
  inputBudget: number;
  /**
   * The filing call's layout for this model (`filingLayoutFor`); absent, `json`. Only a
   * transport that passes `grammar` on can be given `lines`.
   */
  filingLayout?: FilingLayout;
  send(request: ModelRequest): Promise<ModelResponse>;
}

/** What differs between runners: the phone, the server, the web, a benchmark. */
export interface Ports {
  transport?: ModelTransport;
  companyLookup?: (orgNumber: string) => Promise<CompanyInfo | null>;
  /** Runs each step, for a caller that meters or labels them. */
  around?: <T>(stepId: string, work: () => Promise<T>) => Promise<T>;
}
